/** Every customer-visible case in the company, for the sidebar rail. Here, not src/queries: lane rule (CLAUDE.md Scope). */
import * as React from "react";
import { useQuery } from "@tanstack/react-query";

import api from "@/api/base";
import { useYc, ycCase } from "@/pages/Portal/ActionPlan/ycDemo/ycFlow";
import { usePortalFeaturesQuery } from "./usePortalFeatures";

export interface RailCase {
  caseId: string;
  buildingId: string;
  buildingLabel: string;
  caseReference: string;
  caseDisplaySubject: string;
  patternKey: string;
  status: string;
  title: string;
}

export const PORTFOLIO_CASES_KEY = ["portfolio", "violation-cases"] as const;

/** YC demo, delete after the demo (Leo 10-30): switch on and accepted puts its case first. */
const useYcRail = (): ((rows: RailCase[]) => RailCase[]) => {
  const on = usePortalFeaturesQuery(false).data?.ycDemo === true;
  const s = useYc();
  return React.useCallback(
    (rows: RailCase[]) => {
      if (!on || !s.accepted || !s.buildingId || !s.buildingLabel) return rows;
      const c = ycCase(s);
      return [
        {
          caseId: c.caseId,
          buildingId: s.buildingId,
          buildingLabel: s.buildingLabel,
          caseReference: c.caseReference ?? "",
          caseDisplaySubject: c.caseDisplaySubject ?? "",
          patternKey: c.patternKey ?? "",
          status: c.status,
          title: c.title ?? "",
        },
        ...rows,
      ];
    },
    [on, s],
  );
};

/** 404 while the violation_remediation flag is off: `data` stays undefined and the rail draws nothing. */
export const usePortfolioCasesQuery = () => {
  const yc = useYcRail();
  const select = React.useCallback(
    (d: { cases: RailCase[] }) => yc(d.cases ?? []),
    [yc],
  );
  return useQuery({
    queryKey: PORTFOLIO_CASES_KEY,
    queryFn: (): Promise<{ cases: RailCase[] }> =>
      api.get("tracking/portal/portfolio/violation-cases/").then((r) => r.data),
    staleTime: 1000 * 60 * 5,
    retry: false,
    select,
  });
};
